import React from 'react';
import { Select } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_HUB_LIST, GetHubListResponse } from '../../pages/wms/bags/graphql/queries/getHubList';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../utils/languageUtils';

interface MultiPickupHubSelectFilterProps {
  label: string;
  value?: string[];
  onChange: (value: string[] | undefined) => void;
  spanClassName?: string;
  placeholder?: string;
  className?: string;
}

export const MultiPickupHubSelectFilter: React.FC<MultiPickupHubSelectFilterProps> = ({
  label,
  value,
  onChange,
  spanClassName,
  placeholder = 'All',
  className
}) => {
  const { data, loading } = useQuery<GetHubListResponse>(GET_HUB_LIST);

  const hubOptions = React.useMemo(() => {
    if (!data?.hubList) return [];

    return data.hubList
      .filter(hub => hub.sortingCode) // 过滤掉没有sortingCode的hub
      .map(hub => {
        // 提取一段码（sorting code的第一段）
        const firstSegment = hub.sortingCode?.split('-')[0] || '';
        return {
          label: `${hub.name} (${firstSegment})`,
          value: hub.id, // 返回HUB的ID
          code: firstSegment, // Sorting-name（第一段码）
          // 用于搜索的文本，包含名称、ID和代码
          searchText: `${hub.name} ${firstSegment} ${hub.sortingCode} ${hub.id}`
        };
      })
      .filter(opt => opt.value); // 过滤空值
  }, [data]);

  // 使用统一的样式 Hook，根据语言自动返回合适的样式类名
  const containerClassName = useFilterContainerClassName(className);
  const labelClassName = useFilterLabelClassName(spanClassName);
  const inputContainerClassName = useFilterInputContainerClassName();

  return (
    <div className={containerClassName}>
      <span className={labelClassName}>{label}</span>
      <div className={inputContainerClassName}>
        <Select
          mode="multiple"
          showSearch
          allowClear
          placeholder={placeholder}
          value={value}
          onChange={(val) => onChange(val && val.length > 0 ? val : undefined)}
          loading={loading}
          options={hubOptions}
          filterOption={(input, option) => {
            const searchText = (option as any)?.searchText || '';
            return searchText.toLowerCase().includes(input.toLowerCase());
          }}
          className="w-full"
          size="middle"
          maxTagCount="responsive"
          maxTagTextLength={10}
          tagRender={(props) => {
            const { label, closable, onClose } = props;
            return (
              <span className="ant-select-selection-item inline-flex items-center bg-gray-100 text-gray-800 rounded px-2 py-1 text-xs mr-1 mb-1">
                <span className="ant-select-selection-item-content truncate max-w-[120px]">{label}</span>
                {closable && (
                  <span 
                    className="ant-select-selection-item-remove ml-1 cursor-pointer hover:text-red-500" 
                    onClick={onClose}
                  >
                    ×
                  </span>
                )}
              </span>
            );
          }}
        />
      </div>
    </div>
  );
};

export default MultiPickupHubSelectFilter;
